import {
  PersonAdd,
  WarningAmberOutlined,
  PersonOutline,
  CalendarTodayOutlined,
  Visibility,
  SendOutlined,
  CheckCircleOutlined,
  ContentCopy,
  BusinessOutlined,
} from '@mui/icons-material';
import {
  Button,
  CardContainer,
  MetricCard,
  SearchBar,
  Dropdown,
  DataTable,
  Tag,
} from 'finara-components';
import type { DropdownOption, DataTableColumn } from 'finara-components';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { AddDepartmentModal } from '@/components/admin/employees/AddDepartmentModal';
import { AddEmployeeModal } from '@/components/admin/employees/AddEmployeeModal';
import { ConfirmDeleteModal } from '@/components/admin/employees/ConfirmDeleteModal';
import { EditEmployeeModal } from '@/components/admin/employees/EditEmployeeModal';
import { EmployeeActionsMenu } from '@/components/admin/employees/EmployeeActionsMenu';
import { Navbar } from '@/components/admin/general/Navbar';
import { ErrorState, LoadingState, EmptyState, CsvExportButton } from '@/components/common';
import { adminNavItems } from '@/data/admin/general/navigationData';
import { useEmployeeAnalytics, useOrganizationInsights } from '@/hooks/queries/useAnalytics';
import { useDepartments, useCreateDepartment } from '@/hooks/queries/useDepartments';
import { useCreateEmployee } from '@/hooks/queries/useEmployees';
import { useCreateJobTitle, useUpdateJobTitle } from '@/hooks/queries/useJobTitles';
import { useCreateSalary, useUpdateSalary } from '@/hooks/queries/useSalaries';
import {
  useUpdateUser,
  useToggleUserActive,
  useDeleteUser,
  useUserDetails,
} from '@/hooks/queries/useUsers';
import { useAuth } from '@/hooks/useAuth';
import type { EmployeeData } from '@/types/analytics';
import type { CreateEmployeeData } from '@/types/employees';
import type { UpdateUserData } from '@/types/user';
import { formatNumber } from '@/utils/formatters';

export const EmployeeDirectoryPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');

  // Fetch full user details to get organization code
  const { data: userDetails } = useUserDetails(user?.id);

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddDepartmentModalOpen, setIsAddDepartmentModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeData | null>(null);

  // Fetch data from backend
  const {
    data: analyticsData,
    isLoading: isLoadingAnalytics,
    error: analyticsError,
  } = useEmployeeAnalytics();
  const {
    data: insightsData,
    isLoading: isLoadingInsights,
    error: insightsError,
  } = useOrganizationInsights();
  const { data: departmentsData } = useDepartments();

  // Mutations
  const createEmployee = useCreateEmployee();
  const createDepartment = useCreateDepartment();
  const updateUser = useUpdateUser();
  const createSalary = useCreateSalary();
  const updateSalary = useUpdateSalary();
  const createJobTitle = useCreateJobTitle();
  const updateJobTitle = useUpdateJobTitle();
  const toggleUserActive = useToggleUserActive();
  const deleteUser = useDeleteUser();

  const employees = useMemo(() => analyticsData?.employees || [], [analyticsData]);
  const totalEmployees = analyticsData?.total || 0;
  const activeEmployeesCount = insightsData?.activeEmployees || 0;
  const kycPendingCount = insightsData?.pendingKyc || 0;
  const activeAdvancesCount = insightsData?.activeAdvances || 0;
  const monthlyPayroll = insightsData?.monthlyPayroll || 0;

  // Build department options from backend data
  const departmentOptions: DropdownOption[] = useMemo(() => {
    const options: DropdownOption[] = [{ value: '', label: 'Todos los departamentos' }];

    // Access the data array from paginated response
    if (departmentsData?.data && Array.isArray(departmentsData.data)) {
      departmentsData.data.forEach((dept: { id: string; name: string }) => {
        options.push({ value: dept.name, label: dept.name });
      });
    }

    return options;
  }, [departmentsData]);

  const statusOptions: DropdownOption[] = [
    { value: '', label: 'Todos los estados' },
    { value: 'active', label: 'Activo' },
    { value: 'inactive', label: 'Inactivo' },
    { value: 'pending', label: 'Pendiente' },
  ];

  // Client-side filtering
  const filteredEmployees = useMemo(() => {
    return employees.filter((employee: EmployeeData) => {
      // Don't show super_admin users
      if (employee.role && employee.role.toLowerCase() === 'super_admin') {
        return false;
      }

      const matchesSearch = searchTerm
        ? employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.id.toLowerCase().includes(searchTerm.toLowerCase())
        : true;

      const matchesStatus = statusFilter
        ? (employee.isActive && statusFilter === 'active') ||
          (!employee.isActive && statusFilter === 'inactive')
        : true;

      const matchesDepartment = departmentFilter ? employee.department === departmentFilter : true;

      return matchesSearch && matchesStatus && matchesDepartment;
    });
  }, [employees, searchTerm, statusFilter, departmentFilter]);

  const getStatusVariant = (isActive: boolean): 'green' | 'gray' => {
    return isActive ? 'green' : 'gray';
  };

  const getStatusLabel = (isActive: boolean): string => {
    return isActive ? 'Activo' : 'Inactivo';
  };

  const getKYCVariant = (kycCompleted: boolean): 'green' | 'red' => {
    return kycCompleted ? 'green' : 'red';
  };

  const getKYCLabel = (kycCompleted: boolean): string => {
    return kycCompleted ? 'Completo' : 'Pendiente';
  };

  const isAdminUser = (role?: string): boolean => {
    return role?.toLowerCase() === 'admin';
  };

  const formatEmployeeId = (id: string): string => {
    // Format ID as EMP-XXX (take first 8 chars of UUID)
    return `EMP-${id.substring(0, 8).toUpperCase()}`;
  };

  // Handlers
  const handleAddEmployee = async (data: CreateEmployeeData) => {
    try {
      await createEmployee.mutateAsync(data);
      setIsAddModalOpen(false);
      toast.success('Empleado creado exitosamente');
    } catch (_error) {
      toast.error('Error al crear el empleado. Intenta nuevamente.');
    }
  };

  const handleAddDepartment = async (data: { name: string; description?: string }) => {
    try {
      await createDepartment.mutateAsync(data);
      setIsAddDepartmentModalOpen(false);
      toast.success('Departamento creado exitosamente');
    } catch (_error) {
      toast.error('Error al crear el departamento. Intenta nuevamente.');
    }
  };

  const handleEditEmployee = async (
    data: UpdateUserData,
    salaryData?: {
      salaryId?: string;
      amount: number;
      type: string;
      currency?: string;
      frequency?: string;
    },
    jobTitleData?: {
      jobTitleId?: string;
      name: string;
      description?: string;
    }
  ) => {
    if (!selectedEmployee) return;

    try {
      // Update user data
      if (Object.keys(data).length > 0) {
        await updateUser.mutateAsync({
          userId: selectedEmployee.id,
          data,
        });
      }

      // Create or update salary
      if (salaryData) {
        if (salaryData.salaryId) {
          // Update existing salary
          await updateSalary.mutateAsync({
            id: salaryData.salaryId,
            userId: selectedEmployee.id,
            data: {
              amount: salaryData.amount,
              type: salaryData.type as 'base' | 'bonus' | 'commission' | 'overtime' | 'deduction',
            },
          });
        } else {
          // Create new salary
          await createSalary.mutateAsync({
            userId: selectedEmployee.id,
            amount: salaryData.amount,
            currency: (salaryData.currency as 'MXN' | 'USD' | 'EUR' | undefined) ?? 'MXN',
            frequency: (salaryData.frequency as 'monthly' | 'biweekly' | undefined) ?? 'monthly',
            type: salaryData.type as 'base' | 'bonus' | 'commission' | 'overtime' | 'deduction',
          });
        }
      }

      // Create or update job title
      if (jobTitleData) {
        if (jobTitleData.jobTitleId) {
          // Update existing job title
          await updateJobTitle.mutateAsync({
            id: jobTitleData.jobTitleId,
            userId: selectedEmployee.id,
            data: {
              name: jobTitleData.name,
              description: jobTitleData.description,
            },
          });
        } else {
          // Create new job title
          await createJobTitle.mutateAsync({
            userId: selectedEmployee.id,
            name: jobTitleData.name,
            description: jobTitleData.description,
          });
        }
      }

      setIsEditModalOpen(false);
      setSelectedEmployee(null);
      toast.success('Empleado actualizado exitosamente');
    } catch (error) {
      if (error instanceof Error && error.message.includes('already exists')) {
        toast.error('Este correo electrónico ya está en uso. Usa otro correo.');
      } else {
        toast.error('Error al actualizar el empleado. Intenta nuevamente.');
      }
    }
  };

  const handleToggleActive = async (employee: EmployeeData) => {
    try {
      await toggleUserActive.mutateAsync({
        userId: employee.id,
        data: { isActive: !employee.isActive },
      });
      toast.success(`Empleado ${employee.isActive ? 'desactivado' : 'activado'} exitosamente`);
    } catch (_error) {
      toast.error('Error al cambiar el estado del empleado. Intenta nuevamente.');
    }
  };

  const handleDeleteEmployee = async () => {
    if (!selectedEmployee) return;

    try {
      await deleteUser.mutateAsync(selectedEmployee.id);
      setIsDeleteModalOpen(false);
      setSelectedEmployee(null);
      toast.success('Empleado eliminado exitosamente');
    } catch (_error) {
      toast.error('Error al eliminar el empleado. Intenta nuevamente.');
    }
  };

  const openEditModal = (employee: EmployeeData) => {
    setSelectedEmployee(employee);
    setIsEditModalOpen(true);
  };

  const openDeleteModal = (employee: EmployeeData) => {
    setSelectedEmployee(employee);
    setIsDeleteModalOpen(true);
  };

  const handleCopyOrgCode = async () => {
    const orgCode = userDetails?.organization?.code;
    if (!orgCode) {
      toast.error('Código de organización no disponible');
      return;
    }

    try {
      await navigator.clipboard.writeText(orgCode);
      toast.success('Código copiado al portapapeles');
    } catch (_error) {
      toast.error('Error al copiar el código');
    }
  };

  const columns: DataTableColumn<EmployeeData>[] = [
    {
      key: 'employee',
      label: 'Empleado',
      render: (_, employee) => (
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-brand-blue/30 rounded-full flex items-center justify-center">
              <span className="text-sm font-semibold text-brand-white">
                {employee.name
                  .split(' ')
                  .map(n => n[0])
                  .join('')
                  .toUpperCase()}
              </span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-brand-white">{employee.name}</span>
                {isAdminUser(employee.role) && (
                  <Tag variant="black" className="text-xs">
                    Admin
                  </Tag>
                )}
              </div>
              <div className="text-sm text-muted">{employee.email}</div>
              <div className="text-xs text-muted">{formatEmployeeId(employee.id)}</div>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'department',
      label: 'Departamento',
      render: (_, employee) => <span className="text-sm text-brand-white">{employee.department}</span>,
    },
    {
      key: 'position',
      label: 'Puesto',
      render: (_, employee) => <span className="text-sm text-brand-white">{employee.jobTitle}</span>,
    },
    {
      key: 'status',
      label: 'Estado',
      render: (_, employee) => (
        <Tag variant={getStatusVariant(employee.isActive)}>{getStatusLabel(employee.isActive)}</Tag>
      ),
    },
    {
      key: 'kyc',
      label: 'KYC',
      render: (_, employee) => (
        <Tag variant={getKYCVariant(employee.kycCompleted)}>
          {getKYCLabel(employee.kycCompleted)}
        </Tag>
      ),
    },
    {
      key: 'salary',
      label: 'Salario Mensual',
      render: (_, employee) => (
        <span className="text-sm text-brand-white">
          {employee.salaryCurrency} ${formatNumber(employee.monthlySalary)}
        </span>
      ),
    },
    {
      key: 'lastPayment',
      label: 'Último Pago',
      render: (_, employee) => (
        <span className="text-sm text-muted">
          {employee.lastPayrollPayment
            ? new Date(employee.lastPayrollPayment).toLocaleDateString('es-MX')
            : 'N/A'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (_, employee) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => void navigate(`/empleados/directorio/${employee.id}`)}
            className="p-2 text-muted hover:text-brand-white hover:bg-black/5 dark:hover:bg-white/5 rounded"
            title="Ver detalles"
          >
            <Visibility fontSize="small" />
          </button>
          <EmployeeActionsMenu
            onEdit={() => openEditModal(employee)}
            onToggleActive={() => void handleToggleActive(employee)}
            onDelete={() => openDeleteModal(employee)}
            isActive={employee.isActive}
          />
        </div>
      ),
    },
  ];

  // Error state
  if (analyticsError || insightsError) {
    return (
      <div className="min-h-screen">
        <Navbar
          navItems={adminNavItems}
          userName={user?.name || 'Usuario'}
          pageTitle="Gestión de Empleados"
          section="Empleados"
        />
        <ErrorState title="Error al cargar los datos" showRetry />
      </div>
    );
  }

  // Loading state
  if (isLoadingAnalytics || isLoadingInsights) {
    return (
      <div className="min-h-screen">
        <Navbar
          navItems={adminNavItems}
          userName={user?.name || 'Usuario'}
          pageTitle="Gestión de Empleados"
          section="Empleados"
        />
        <LoadingState message="Cargando datos..." />
      </div>
    );
  }

  // Empty state
  if (!analyticsData || !insightsData) {
    return (
      <div className="min-h-screen">
        <Navbar
          navItems={adminNavItems}
          userName={user?.name || 'Usuario'}
          pageTitle="Gestión de Empleados"
          section="Empleados"
        />
        <EmptyState
          Icon={<PersonOutline />}
          title="No hay datos disponibles"
          description="No se encontraron datos. Intenta nuevamente."
          showAction
          actionText="Recargar"
          onAction={() => window.location.reload()}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar
        navItems={adminNavItems}
        userName={user?.name || 'Usuario'}
        pageTitle="Gestión de Empleados"
        section="Empleados"
      />
      <div className="px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="lg:flex items-center justify-between my-4">
          <div className="mb-4 lg:!mb-0">
            <h1 className="text-2xl font-bold text-brand-white flex items-center gap-2">
              <PersonOutline />
              Gestión de Empleados
            </h1>
            <p className="text-muted mt-1">
              {filteredEmployees.length} de {totalEmployees} empleados · {activeEmployeesCount}{' '}
              activos · {kycPendingCount} con KYC pendiente
            </p>
          </div>
          <div className="flex items-center gap-3">
            <CsvExportButton
              data={filteredEmployees}
              filename="empleados"
              columns={employee => ({
                ID: formatEmployeeId(employee.id),
                Nombre: employee.name,
                Email: employee.email,
                Departamento: employee.department,
                Puesto: employee.jobTitle,
                Estado: getStatusLabel(employee.isActive),
                KYC: getKYCLabel(employee.kycCompleted),
                'Salario Mensual': `${employee.salaryCurrency} ${formatNumber(employee.monthlySalary)}`,
                'Último Pago': employee.lastPayrollPayment
                  ? new Date(employee.lastPayrollPayment).toLocaleDateString('es-MX')
                  : 'N/A',
              })}
              variant="white"
              size="small"
              buttonText="Exportar"
            />
            <Button
              variant="white"
              startIcon={<BusinessOutlined />}
              size="small"
              onClick={() => setIsAddDepartmentModalOpen(true)}
            >
              Crear Departamento
            </Button>
            <Button
              variant="blue"
              startIcon={<PersonAdd />}
              size="small"
              onClick={() => setIsAddModalOpen(true)}
            >
              Agregar Empleado
            </Button>
          </div>
        </div>
        <div className="flex text-md items-center gap-2">
          <span className="text-md font-semibold text-brand-white">Código de Organización:</span>
          <span className="text-md text-brand-white">{userDetails?.organization?.code || 'N/A'}</span>
          {userDetails?.organization?.code && (
            <button
              onClick={() => void handleCopyOrgCode()}
              className="ml-1 p-1 hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors"
              title="Copiar código"
            >
              <ContentCopy fontSize="inherit" className="text-muted" />
            </button>
          )}
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 md:!grid-cols-4 gap-4 mb-6">
        <MetricCard
          title="Empleados Activos"
          value={activeEmployeesCount.toString()}
          icon={<CheckCircleOutlined className="text-brand-blue" />}
          variant="white"
        />
        <MetricCard
          title="KYC Pendientes"
          value={kycPendingCount.toString()}
          icon={<WarningAmberOutlined className="text-yellow-500" />}
          variant="white"
        />
        <MetricCard
          title="Adelantos Activos"
          value={activeAdvancesCount.toString()}
          icon={<SendOutlined className="text-green-500" />}
          variant="white"
        />
        <MetricCard
          title="Nómina Mensual"
          value={`$${formatNumber(monthlyPayroll / 1000)}K`}
          icon={<CalendarTodayOutlined className="text-purple-500" />}
          variant="white"
        />
      </div>

      {/* Filters */}
      <CardContainer>
        <div>
          <div className="flex flex-col lg:!flex-row lg:items-center gap-4 mb-6">
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Buscar por nombre, email o ID de empleado..."
              className="w-full lg:!w-auto flex-1"
            />
            <Dropdown
              id="status-filter"
              value={statusFilter}
              onChange={value => setStatusFilter(String(value))}
              options={statusOptions}
              placeholder="Todos los estados"
              className="w-full lg:!w-auto"
            />
            <Dropdown
              id="department-filter"
              value={departmentFilter}
              onChange={value => setDepartmentFilter(String(value))}
              options={departmentOptions}
              placeholder="Todos los departamentos"
              className="w-full lg:!w-auto"
              searchable
            />
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {userDetails?.organization?.name && (
              <h3 className="text-sm font-semibold text-brand-white mb-4">
                Empleados de {userDetails.organization.name}
              </h3>
            )}
            {filteredEmployees.length > 0 ? (
              <DataTable columns={columns} data={filteredEmployees} rowClassName={(_, index) => (index % 2 === 0 ? 'dark:bg-brand-blue/10' : '')} />
            ) : (
              <EmptyState
                Icon={<PersonOutline />}
                title="No se encontraron empleados"
                description={
                  searchTerm || statusFilter || departmentFilter
                    ? 'Intenta ajustar los filtros de búsqueda'
                    : 'Agrega tu primer empleado para comenzar'
                }
                useCard={false}
                className="py-8"
              />
            )}
          </div>
        </div>
      </CardContainer>
      </div>

      {/* Modals */}
      <AddDepartmentModal
        isOpen={isAddDepartmentModalOpen}
        onClose={() => setIsAddDepartmentModalOpen(false)}
        onSubmit={data => void handleAddDepartment(data)}
        isLoading={createDepartment.isPending}
      />

      <AddEmployeeModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={data => void handleAddEmployee(data)}
        isLoading={createEmployee.isPending}
      />

      <EditEmployeeModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedEmployee(null);
        }}
        onSubmit={(userData, salaryData, jobTitleData) =>
          void handleEditEmployee(userData, salaryData, jobTitleData)
        }
        employee={selectedEmployee}
        isLoading={
          updateUser.isPending ||
          createSalary.isPending ||
          updateSalary.isPending ||
          createJobTitle.isPending ||
          updateJobTitle.isPending
        }
      />

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedEmployee(null);
        }}
        onConfirm={() => void handleDeleteEmployee()}
        employeeName={selectedEmployee?.name || ''}
        isLoading={deleteUser.isPending}
      />
    </div>
  );
};
